import React from 'react';
import { useLang } from '../../i18n';
import { useLists } from '../../context';
import { useListNames } from '../../hooks/useListNames';
import { useListActions } from './useListActions';

/**
 * ListsManager — the Today board's "✏️ Edit lists" view (same idea as the Closing tab's
 * ✏️ Edit). Every list as one compact row, in board order: ▲▼ to reorder, ✏️ to open
 * its settings (name / icon / colour / move items), 🗑 to delete one you made — and a
 * big "＋ Add a new list" on top.
 */
export function ListsManager({ boardItems, allItems, author, onAdd, onEdit }) {
  const { t } = useLang();
  const { active } = useLists();
  const { name } = useListNames();
  const actions = useListActions({ boardItems, allItems, author });
  const openCount = id => boardItems.filter(i => i.category === id).length;

  return (
    <div className="do-lm">
      <button className="do-lm__add" onClick={onAdd}>
        <span className="do-newlist__plus" aria-hidden="true">＋</span>
        <span className="do-newlist__txt">
          <b className="do-newlist__title">{t('lists.addNew')}</b>
          <span className="do-newlist__sub">{t('lists.newHint')}</span>
        </span>
      </button>
      <p className="do-lm__hint">{t('lists.manageHint')}</p>
      <ol className="do-lm__rows">
        {active.map((l, i) => {
          const n = openCount(l.id);
          return (
            <li key={l.id} className="do-lm__row" style={{ '--cc': l.color }}>
              <span className="do-lm__icon" aria-hidden="true">{l.icon}</span>
              <span className="do-lm__main">
                <b className="do-lm__name">{name(l)}</b>
                <span className="do-lm__meta">
                  {l.sourceable ? t('lists.kindBuyShort') : t('lists.kindTaskShort')}
                  {' · '}{t('lists.openN', { n })}
                  {l.builtin && <> · {t('lists.builtin')}</>}
                </span>
              </span>
              <span className="do-lm__tools">
                <button className="do-cl__icon-btn" onClick={() => actions.move(l, -1)} disabled={i === 0}
                  aria-label={t('lists.moveUpAria', { name: name(l) })} title={t('lists.moveUpAria', { name: name(l) })}>▲</button>
                <button className="do-cl__icon-btn" onClick={() => actions.move(l, 1)} disabled={i === active.length - 1}
                  aria-label={t('lists.moveDownAria', { name: name(l) })} title={t('lists.moveDownAria', { name: name(l) })}>▼</button>
                <button className="do-lm__edit" onClick={() => onEdit(l)} aria-label={t('lists.editAria', { name: name(l) })}>
                  {t('lists.editBtn')}
                </button>
                {l.custom && (
                  <button className="do-cl__icon-btn do-cl__icon-btn--danger" onClick={() => actions.remove(l)}
                    aria-label={t('lists.deleteAria', { name: name(l) })} title={t('lists.deleteAria', { name: name(l) })}>🗑</button>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
